import { Literal, Static, Union } from "runtypes";
import { z } from "zod";

import {
  AgentChatRunId,
  EditSessionBranchId,
  TaoProjectId,
} from "../../idTypeBrands.js";
import { getNormalEnum } from "../../runtypeEnums.js";

import { AgentChatMessageSource } from "./agentChatMessageTypes.js";
import { AGENT_CHAT_MESSAGE_SOURCE_DISPLAY_NAME } from "./source";

export const AgentChatThreadTypeLiteral = Union(
  // MagicEvent-based threads
  // AgentChatMessage-based threads
  Literal("LOGIC_VIEW_V2"),
  Literal("ASK"),
  Literal("REVIEW"),
  Literal("ARTIFACT_EXTRACTION"),
  Literal("HERO_ANALYZER"),
  Literal("HERO_AGGREGATOR"),
  Literal("KNOWLEDGE_CURATION"),
  Literal("SEMANTIC_AUTHORING"),
  // Unused/dormant
  Literal("LOGIC_VIEW"),
  Literal("CHAT_WITH_APP"),
  Literal("EXPLORE"),
  Literal("CODE_PROJECT"),
);
export type AgentChatThreadType = Static<typeof AgentChatThreadTypeLiteral>;
export const AgentChatThreadType = getNormalEnum(AgentChatThreadTypeLiteral);

// TODO: add AgentChatThreadType.ANALYTICS when that gets added
export const ANALYTICS_AGENT_TYPES: AgentChatThreadType[] = [
  AgentChatThreadType.LOGIC_VIEW_V2,
  AgentChatThreadType.ASK,
];

export const AgentChatThreadAutoSaveHexVersionStatusLiteral = Union(
  Literal("PENDING"),
  Literal("SUCCEEDED"),
  Literal("FAILED"),
);
export type AgentChatThreadAutoSaveHexVersionStatus = Static<
  typeof AgentChatThreadAutoSaveHexVersionStatusLiteral
>;
export const AgentChatThreadAutoSaveHexVersionStatus = getNormalEnum(
  AgentChatThreadAutoSaveHexVersionStatusLiteral,
);

/**
 * A classification of a thread, based on what type of work the agent is actually doing.
 */
export const AgentChatThreadClassificationLiteral = Union(
  Literal("ANALYSIS"),
  Literal("APP_BUILDING"),
  Literal("QUICK_QUESTION"),
  Literal("DEEPER_ANALYSIS"),
);
export type AgentChatThreadClassification = Static<
  typeof AgentChatThreadClassificationLiteral
>;
export const AgentChatThreadClassification = getNormalEnum(
  AgentChatThreadClassificationLiteral,
);

/**
 * The collapsed classifications that reads/UI collapse to. `QUICK_QUESTION` and
 * `DEEPER_ANALYSIS` both normalize into `ANALYSIS`.
 */
export const CollapsedAgentChatThreadClassificationLiteral = Union(
  Literal("ANALYSIS"),
  Literal("APP_BUILDING"),
);
export type CollapsedAgentChatThreadClassification = Static<
  typeof CollapsedAgentChatThreadClassificationLiteral
>;
export const CollapsedAgentChatThreadClassification = getNormalEnum(
  CollapsedAgentChatThreadClassificationLiteral,
);

const LEGACY_ANALYSIS_CLASSIFICATIONS: ReadonlySet<string> = new Set([
  "QUICK_QUESTION",
  "DEEPER_ANALYSIS",
]);

/**
 * Collapses old Quick Question/Deeper Analysis classifications into just Analysis.
 */
export function normalizeAgentChatThreadClassification(
  classification: string | null | undefined,
): CollapsedAgentChatThreadClassification | null {
  if (classification == null) {
    return null;
  }
  if (LEGACY_ANALYSIS_CLASSIFICATIONS.has(classification)) {
    return CollapsedAgentChatThreadClassification.ANALYSIS;
  }
  if (CollapsedAgentChatThreadClassificationLiteral.guard(classification)) {
    return classification;
  }
  return null;
}

/**
 * Escalation order for the collapsed classifications: a thread can go
 * from ANALYSIS -> APP_BUILDING but not vice versa.
 */
export const AGENT_CHAT_THREAD_CLASSIFICATION_ORDER: readonly CollapsedAgentChatThreadClassification[] =
  [
    CollapsedAgentChatThreadClassification.ANALYSIS,
    CollapsedAgentChatThreadClassification.APP_BUILDING,
  ];

export function getAgentChatThreadClassificationRank(
  classification: AgentChatThreadClassification,
): number {
  const normalized = normalizeAgentChatThreadClassification(classification);
  return normalized == null
    ? -1
    : AGENT_CHAT_THREAD_CLASSIFICATION_ORDER.indexOf(normalized);
}

/**
 * Get the classification of a thread, given its current classification and the latest one
 * from the artifact extraction agent.
 *
 * Ranking is done on the canonical (collapsed) values, but the original value is
 * returned so that writes preserve whatever shape the caller produced (legacy
 * values during the migration window, `ANALYSIS` afterwards).
 */
export function mergeAgentChatThreadClassification(
  current: AgentChatThreadClassification | null,
  next: AgentChatThreadClassification,
): AgentChatThreadClassification {
  if (current == null) {
    return next;
  }
  return getAgentChatThreadClassificationRank(next) >
    getAgentChatThreadClassificationRank(current)
    ? next
    : current;
}

/**
 * Deterministic thread classification used as a fallback
 *   - LOGIC_VIEW_V2 (notebook) threads    -> APP_BUILDING
 *   - ASK                                 -> ANALYSIS
 */
export function getDeterministicThreadClassification({
  threadType,
}: {
  threadType: AgentChatThreadType;
}): AgentChatThreadClassification | null {
  switch (threadType) {
    case AgentChatThreadType.LOGIC_VIEW_V2:
      return AgentChatThreadClassification.APP_BUILDING;
    case AgentChatThreadType.ASK:
      return AgentChatThreadClassification.ANALYSIS;
    default:
      return null;
  }
}

/**
 * User-facing display names for thread classifications for Context Studio surfaces.
 * Legacy values are shown under the collapsed `Analysis` label.
 */
export const AGENT_CHAT_THREAD_CLASSIFICATION_DISPLAY_NAME: Record<
  AgentChatThreadClassification,
  string
> = {
  [AgentChatThreadClassification.ANALYSIS]: "Analysis",
  [AgentChatThreadClassification.APP_BUILDING]: "App building",
  [AgentChatThreadClassification.QUICK_QUESTION]: "Analysis",
  [AgentChatThreadClassification.DEEPER_ANALYSIS]: "Analysis",
};

export const getAgentChatThreadClassificationDisplayName = (
  classification: AgentChatThreadClassification,
): string => {
  const normalized = normalizeAgentChatThreadClassification(classification);
  return normalized == null
    ? "Unknown"
    : AGENT_CHAT_THREAD_CLASSIFICATION_DISPLAY_NAME[normalized];
};

/**
 * Reason why a thread is returned as an `UnknownAgentChatThread`:
 *  - `SENSITIVE`: the thread is private, and contents are hidden from everyone except the user who created it.
 *  - `ACCESS_DOWNGRADED`: the user does not have permission to view this thread
 *    (e.g. they lack access to a data connection used within the thread, secret, etc.).
 */
export const AgentChatThreadRestrictedReasonLiteral = Union(
  Literal("SENSITIVE"),
  Literal("ACCESS_DOWNGRADED"),
);
export type AgentChatThreadRestrictedReason = Static<
  typeof AgentChatThreadRestrictedReasonLiteral
>;
export const AgentChatThreadRestrictedReason = getNormalEnum(
  AgentChatThreadRestrictedReasonLiteral,
);

/**
 * User-facing display names for each `AgentChatThreadType`.
 */
export const AGENT_CHAT_THREAD_TYPE_DISPLAY_NAME: Record<
  AgentChatThreadType,
  string
> = {
  [AgentChatThreadType.ASK]: "Threads",
  [AgentChatThreadType.LOGIC_VIEW]: "Notebook",
  [AgentChatThreadType.LOGIC_VIEW_V2]: "Notebook",
  [AgentChatThreadType.REVIEW]: "Review",
  [AgentChatThreadType.ARTIFACT_EXTRACTION]: "Artifact Extraction",
  [AgentChatThreadType.HERO_ANALYZER]: "Hero Analyzer",
  [AgentChatThreadType.HERO_AGGREGATOR]: "Hero Aggregator",
  [AgentChatThreadType.KNOWLEDGE_CURATION]: "Knowledge Curation",
  [AgentChatThreadType.SEMANTIC_AUTHORING]: "Modeling",
  [AgentChatThreadType.EXPLORE]: "Explore",
  [AgentChatThreadType.CHAT_WITH_APP]: "Published app",
  [AgentChatThreadType.CODE_PROJECT]: "Code project agent",
};

/**
 * Types that only appear as subagent identifiers, never as AgentChatThread.type in the DB.
 */
export const AgentSubagentTypeLiteral = Union(
  Literal("DATA_DISCOVERY"),
  Literal("VISUALIZATION"),
  Literal("DATA_APP"),
);
export type AgentSubagentType = Static<typeof AgentSubagentTypeLiteral>;
export const AgentSubagentType = getNormalEnum(AgentSubagentTypeLiteral);

/**
 * Combined type covering both user-facing thread types and subagent-only types.
 */
export type AgentChatThreadOrSubagentType =
  | AgentChatThreadType
  | AgentSubagentType;

export const AgentLocationTypeLiteral = Union(
  Literal("HOMEPAGE"),
  Literal("EDIT_SESSION_BRANCH"),
  Literal("EXTERNAL"),
);
export type AgentLocationType = Static<typeof AgentLocationTypeLiteral>;
export const AgentLocationType = getNormalEnum(AgentLocationTypeLiteral);

/**
 * Structured location describing where a tao-project message was sent from.
 *
 * - HOMEPAGE: sent from the Hex homepage; no location reference
 * - EDIT_SESSION_BRANCH: sent from the edit view of a plain-code project;
 *   referenceId is an EditSessionBranchId, from which the TaoProjectId
 *   can be derived via edit_session_branch.projectId
 * - EXTERNAL: sent from outside the app (CLI / API); no entity reference
 */
export type AgentLocation =
  | {
      type: typeof AgentLocationType.HOMEPAGE;
      projectId?: TaoProjectId | null;
      projectTitle?: string | null;
    }
  | {
      type: typeof AgentLocationType.EDIT_SESSION_BRANCH;
      branchId: EditSessionBranchId;
      projectId?: TaoProjectId | null;
      projectTitle?: string | null;
      branchName?: string | null;
    }
  | { type: typeof AgentLocationType.EXTERNAL };

export const INTERNAL_BACKGROUND_AGENT_CHAT_THREAD_TYPES = [
  AgentChatThreadType.REVIEW,
  AgentChatThreadType.ARTIFACT_EXTRACTION,
  AgentChatThreadType.KNOWLEDGE_CURATION,
  AgentChatThreadType.HERO_ANALYZER,
  AgentChatThreadType.HERO_AGGREGATOR,
] as const satisfies readonly AgentChatThreadType[];

export type LogicViewChatState = "closed" | "chat" | "history";
export const AgentEntryPointLiteral = Union(
  Literal("FIX_CELL_WITH_AGENT"),
  Literal("SLACK"),
  Literal("MCP"),
  Literal("AGENT_SUGGESTION"),
  Literal("CONTEXT_WORKBENCH_PREVIEW"),
  Literal("SEMANTIC_PROJECT_WORKBENCH_PREVIEW"),
  Literal("PUBLISHED_APP"),
  Literal("PUBLIC_API"),
  Literal("TASK"),
  Literal("CUSTOMER_EVAL"),
  Literal("HOMEPAGE"),
  Literal("PROJECT_THREAD_FULLSCREEN"),
  Literal("PROJECT_THREAD_NOTEBOOK_SPLIT"),
);
export type AgentEntryPoint = Static<typeof AgentEntryPointLiteral>;
export const AgentEntryPoint = getNormalEnum(AgentEntryPointLiteral);

export type AppAgentChatState = "closed" | "chat" | "history";

/**
 * Context for subagent message isolation.
 * Contains both the subagent type and a unique invocation ID.
 */
export interface SubagentContext {
  subagentType: AgentSubagentType;
  subagentInvocationId: AgentChatRunId;
}

/**
 * Status for queued prompts in an agent chat thread.
 */
export const QueuedPromptStatusLiteral = Union(
  /** Prompt is waiting to be processed */
  Literal("PENDING"),
  /** Prompt is marked to run next, ahead of the normal queue */
  Literal("FORCED"),
);
export type QueuedPromptStatus = Static<typeof QueuedPromptStatusLiteral>;
export const QueuedPromptStatus = getNormalEnum(QueuedPromptStatusLiteral);

/**
 * Upper bound on the number of attachments (mentioned tables, datasets, views,
 * projects, cells, and data connections) that can be sent when creating a
 * thread. Defends against unnecessary prompt bloat since attachments are
 * inlined into the prompt.
 */
export const MAX_PROMPT_ATTACHMENTS = 150;

/**
 * Source of a thread as exposed via the public/external APIs.
 *
 * TODO(@acorn): consider merging this with {@link AgentChatMessageSource}. As
 * of this writing, it's unclear if these should be 1:1 and we want to be
 * intentional about what we expose via the API.
 */
export const ApiThreadsSourceSchema = z.enum([
  "HEX",
  "SLACK",
  "MCP",
  "PUBLIC_API",
]);
export type ApiThreadsSource = z.infer<typeof ApiThreadsSourceSchema>;

export const getAgentChatThreadTypeDisplayName = (
  type: AgentChatThreadType,
): string => {
  return AGENT_CHAT_THREAD_TYPE_DISPLAY_NAME[type] ?? "Unknown";
};

export const getAgentChatMessageSourceDisplayName = (
  source: AgentChatMessageSource | null | undefined,
): string => {
  if (source == null) {
    return AGENT_CHAT_MESSAGE_SOURCE_DISPLAY_NAME[AgentChatMessageSource.HEX];
  }
  return AGENT_CHAT_MESSAGE_SOURCE_DISPLAY_NAME[source];
};
